/**
 * views/reporting/sections/Utilization.jsx - utilization trend and forecast
 * from the Manpower calendar.
 * Rendered by calling it from ReportingView.jsx with the values it reads,
 * so it renders the same DOM the view used to build inline.
 */
import { UtilizationChart } from '../../../components/charts/UtilizationChart';
import { CALENDAR_FAILED } from '../../../hooks/useCalendarUtilization';
import { UtilizationForecastChart } from '../../../components/charts/UtilizationForecastChart';
import { HOURS_PER_DAY } from '../../../utils/utilizationFromCalendar';
import { CARD } from '../reportingTokens';
import { SectionHeader } from '../SectionHeader';

export function renderUtilization({
  calendarFailed,
  utilizationLoaded,
  utilizationData,
  forecastHeadcount,
  capacityNow,
  setForecastMode,
  forecastMode,
  allEngineers,
  calendarEdits,
  calendarDetails,
  calendarLayout,
}) {
  return (
    <>
      {/* UTILIZATION TREND - from the Manpower calendar */}
      <SectionHeader
        title="Engineer Utilization"
        subtitle="From the Manpower calendar · 2 months back, this month, 2 forward · internal engineers in the tables that count"
        explain={{
          title: 'Engineer Utilization',
          body: [
            "How much of the North America team's available time is booked on the Manpower calendar.",
            'The window is five months because the calendar holds future assignments - the two months ahead are real bookings, not a forecast.',
            'Utilised is booked days as a share of sellable capacity. Site and Office split that same number, so the two always add up to it.',
            'PTO leaves the utilisation percentage on both sides - a week off is not capacity that went unsold - but is drawn as its own dashed line, measured against ALL days, so you can still see where the time went.',
            "A day is PTO when its label says so (PTO, vacation, holiday, sick...) or when it was scheduled with Status PTO. One PTO day takes out that day only, never the person's whole month.",
            'Site vs Office takes the Status picked when the block was scheduled (On-Site / Office). Without one it reads the label convention the team already types ("Oshkosh -Site", "GM P15 - Offline"), and an unmarked label falls back to how that engineer usually works - the more common Status on their own scheduled blocks. The card\'s status is only the last resort, and never when it says PTO (it follows today\'s calendar, so it says nothing about other days).',
            'Only engineers in a Manpower calendar table switched to "Counts toward utilization" are counted - Controls and Process to start; any table added later starts switched off. Only internal engineers count: interns, contractors, South Africa, MENA and Europe can be scheduled but are never counted.',
          ].join('\n\n'),
          formula: [
            'util% = booked weekdays / available weekdays',
            '  roster  = internal engineers (not interns, contractors, South Africa, MENA or Europe), not archived, in a calendar table set to count toward utilization (Controls and Process to start)',
            '  weekday = Mon-Fri (weekends never count)',
            '  a day labelled PTO/vacation/holiday, or with Status PTO, leaves BOTH sides',
          ].join('\n'),
        }}
      />
      <div style={{ ...CARD, marginBottom: 20 }}>
        <div style={{ height: 220 }}>
          {/* A calendar that could not be read is said out loud - an empty one
              would draw as 0% booked, which reads as a real number. */}
          {calendarFailed ? (
            <div style={{ fontSize: 11.5, color: '#9AA29B', padding: 20 }}>{CALENDAR_FAILED}</div>
          ) : utilizationLoaded && utilizationData.length === 0 ? (
            <div style={{ fontSize: 11.5, color: '#9AA29B', padding: 20 }}>No internal engineers in the counting calendar tables yet.</div>
          ) : (
            <UtilizationChart data={utilizationData} />
          )}
        </div>
      </div>

      {/* UTILIZATION FORECAST — capacity baseline; demand/forecast wired but
          empty until live quote-hours data is integrated */}
      <SectionHeader
        title="Utilization Forecast"
        subtitle="Engineering capacity vs current + pipeline demand · 3 mo back, 12 mo forward"
        explain={{
          title: 'Utilization Forecast',
          body: `Projects how busy the engineering team will be each month against actual team capacity. Three series:\n\n• Available (orange) — engineer-hours the team can deliver, off the Manpower calendar: each of the ${forecastHeadcount} internal engineers in the calendar tables that count toward utilization (Controls and Process to start; interns, contractors, South Africa, MENA and Europe not counted) × the month's weekdays × ${HOURS_PER_DAY} hrs, less every PTO day on the calendar. Weekends, PTO and interns are out, so the line dips in a month with a lot of time off.\n• Demand (purple) — hours already committed to Won projects.\n• Forecast Demand (blue) — hours that would land if every Open quote in the pipeline wins.\n\nDemand and Forecast are PLACEHOLDERS (zero) until the live quote-hours integration is live. The series are wired so the chart lights up automatically once data lands.`,
          formula: `Available per month = sum over internal engineers in the counting calendar tables, of (weekdays - PTO weekdays) × ${HOURS_PER_DAY} hrs\n  this month: ${utilizationLoaded && !calendarFailed ? `(${forecastHeadcount} × ${capacityNow.weekdays} - ${capacityNow.ptoDays}) × ${HOURS_PER_DAY} = ${capacityNow.hours.toLocaleString()} hrs` : 'the calendar has not loaded'}`,
        }}
        right={
          <div style={{ display: 'flex', background: '#F4F6F0', borderRadius: 8, padding: 3, gap: 2 }}>
            {[
              { id: 'hours', label: 'Hours' },
              { id: 'percent', label: '%' },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setForecastMode(m.id)}
                style={{
                  padding: '5px 14px',
                  borderRadius: 5,
                  border: 'none',
                  background: forecastMode === m.id ? '#1B5E20' : 'transparent',
                  color: forecastMode === m.id ? '#fff' : '#444',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        }
      />
      <div style={{ ...CARD, marginBottom: 20 }}>
        <div style={{ height: 280 }}>
          {/* Drawn once the calendar has answered: before that every month
              would read as full capacity with no PTO taken off. */}
          {calendarFailed ? (
            <div style={{ fontSize: 11.5, color: '#9AA29B', padding: 20 }}>{CALENDAR_FAILED}</div>
          ) : utilizationLoaded ? (
            <UtilizationForecastChart
              mode={forecastMode}
              engineers={allEngineers}
              edits={calendarEdits}
              details={calendarDetails}
              layout={calendarLayout}
            />
          ) : (
            <div style={{ fontSize: 11.5, color: '#9AA29B', padding: 20 }}>Loading the calendar...</div>
          )}
        </div>
        <div style={{ paddingTop: 10, fontSize: 10.5, color: '#888', fontWeight: 500, textAlign: 'right' }}>
          {/* The capacity figure needs the calendar's PTO; without it the
              number would be full headcount with no time off taken out. */}
          {utilizationLoaded && !calendarFailed && (
            <>
              Capacity this month:{' '}
              <b style={{ color: '#E65100' }}>
                {forecastHeadcount} internal engineers × {capacityNow.weekdays} weekdays, less {capacityNow.ptoDays} PTO days, ×{' '}
                {HOURS_PER_DAY} hrs = {capacityNow.hours.toLocaleString()} hrs
              </b>
              {' · '}interns, weekends and PTO not counted
              {' · '}
            </>
          )}
          Demand &amp; Forecast lines populate once live quote-hours integration is live
        </div>
      </div>
    </>
  );
}
